import React, { useMemo, useState, useEffect, useRef } from 'react';
import { MapPin, Plus, ChevronDown, Check } from 'lucide-react';
import indiaData from '../data/india_locations.json';

// Build a flat list of "City, State" entries once.
const ALL_LOCATIONS = (() => {
  const out = [];
  (indiaData.states || []).forEach((s) => {
    (s.cities || []).forEach((c) => out.push(`${c}, ${s.name}`));
  });
  return out.sort((a, b) => a.localeCompare(b));
})();

/**
 * Searchable combobox bound to the canonical India state+city dataset.
 * - Free-form entry allowed (custom "Add" pill when no match)
 * - Value stays a plain string like "Mumbai, Maharashtra"
 *
 * Props:
 *  value: string
 *  onChange: (string) => void
 *  placeholder?: string
 *  testid?: string
 *  required?: boolean
 *  className?: string
 */
export default function LocationCombobox({ value, onChange, placeholder, testid, required, className }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const display = open ? query : (value || '');
  const trimmed = (query || '').trim().toLowerCase();

  const filtered = useMemo(() => {
    if (!trimmed) return ALL_LOCATIONS.slice(0, 40);
    return ALL_LOCATIONS.filter((l) => l.toLowerCase().includes(trimmed)).slice(0, 80);
  }, [trimmed]);

  const exactMatch = useMemo(
    () => ALL_LOCATIONS.find((l) => l.toLowerCase() === trimmed),
    [trimmed]
  );

  const commit = (val) => {
    onChange(val);
    setQuery('');
    setOpen(false);
    setHighlight(0);
  };

  const onKey = (e) => {
    if (!open) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight((h) => Math.min(filtered.length, h + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight((h) => Math.max(0, h - 1)); }
    else if (e.key === 'Enter') {
      e.preventDefault();
      // Highlight 0 = first match · ===filtered.length means "Add new" row when no exact
      if (filtered.length > 0 && highlight < filtered.length) {
        commit(filtered[highlight]);
      } else if (trimmed) {
        commit(query.trim());
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const canAddNew = trimmed && !exactMatch;

  return (
    <div ref={wrapRef} className={`relative ${className || ''}`}>
      <div
        className={`input flex items-center gap-2 cursor-text ${open ? 'ring-2 ring-[#3b4872]/30 border-[#3b4872]' : ''}`}
        onClick={() => { setOpen(true); setTimeout(() => inputRef.current?.focus(), 0); }}
        data-testid={testid || 'location-combobox'}
      >
        <MapPin size={14} className="text-slate-400 shrink-0" />
        <input
          ref={inputRef}
          type="text"
          value={display}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); setHighlight(0); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKey}
          placeholder={placeholder || 'Search city (e.g. Mumbai, Maharashtra)'}
          className="flex-1 bg-transparent outline-none text-sm"
          required={required}
          data-testid={testid ? `${testid}-input` : undefined}
        />
        <ChevronDown size={14} className={`text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </div>

      {open && (
        <div className="absolute z-50 mt-1 left-0 right-0 bg-white border border-slate-200 rounded-sm shadow-lg max-h-72 overflow-y-auto" data-testid="location-dropdown">
          {filtered.length === 0 && !canAddNew && (
            <div className="px-3 py-2 text-xs text-slate-400">Start typing to search 720+ Indian cities…</div>
          )}
          {filtered.map((l, idx) => (
            <button
              type="button"
              key={l}
              onClick={() => commit(l)}
              onMouseEnter={() => setHighlight(idx)}
              className={`w-full flex items-center gap-2 px-3 py-1.5 text-sm text-left ${highlight === idx ? 'bg-indigo-50 text-[#3b4872]' : 'text-slate-700'} hover:bg-indigo-50`}
              data-testid={`location-option-${idx}`}
            >
              <MapPin size={12} className="text-slate-400 shrink-0" />
              <span className="truncate">{l}</span>
              {l === value && <Check size={12} className="ml-auto text-emerald-600" />}
            </button>
          ))}
          {canAddNew && (
            <button
              type="button"
              onClick={() => commit(query.trim())}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm border-t border-slate-100 bg-amber-50/40 hover:bg-amber-100 text-amber-900"
              data-testid="location-add-new"
            >
              <Plus size={13} />
              <span>Add new location: <strong>"{query.trim()}"</strong></span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export { ALL_LOCATIONS };
